<template>
    <main
        id="page-registration"
        class="bg-gray-100"
    >
        <div class="container mx-auto">
            <Breadcrumb title="Konto eröffnen"/>
        </div>

        <form
            class="form-registration container mx-auto lg:grid grid-cols-2 gap-16 py-16"
            @submit.prevent="register"
        >
            <div class="col-span-2">
                <h1 class="text-xl lg:text-5xl">
                    Konto eröffnen
                </h1>
            </div>

            <!-- Panel: Login -->
            <div class="panel">
                <div class="p-8 space-y-8 w-full">
                    <h2 class="h3">
                        Anmeldeinformationen
                    </h2>

                    <UFormField
                        label="E-Mail-Adresse"
                        required
                    >
                        <UInput
                            v-model="form.email"
                            type="email"
                            class="w-full"
                        />
                    </UFormField>

                    <UFormField
                        label="Passwort"
                        required
                    >
                        <UInput
                            v-model="form.plainPassword"
                            type="password"
                            class="w-full"
                        />
                    </UFormField>

                    <UFormField
                        label="Passwort wiederholen"
                        required
                    >
                        <UInput
                            v-model="form.passwordRepeat"
                            type="password"
                            class="w-full"
                        />
                    </UFormField>

                    <p class="block text-xs">
                        Bei den mit einem Sternchen (*) markierten Feldern handelt es sich um Pflichtfelder!
                    </p>

                    <div class="flex items-start gap-2">
                        <UCheckbox
                            v-model="privacy"
                            style="padding-inline: 0 !important; padding-block: 0 !important;"
                        >
                            <template #label>
                                Ich habe die
                                <a
                                    href="/datenschutz"
                                    target="_blank"
                                    class="text-secondary underline"
                                >
                                    Datenschutzhinweise
                                </a>
                                zur Kenntnis genommen! <sup>*</sup>
                            </template>
                        </UCheckbox>
                    </div>
                </div>
            </div>

            <!-- Panel: Adresse -->
            <div class="panel">
                <div class="p-8 space-y-4 w-full">
                    <h2 class="h3">
                        Adresse
                    </h2>

                    <UFormField
                        label="Anrede"
                    >
                        <USelect
                            v-model="form.address"
                            :items="salutationItems"
                            class="w-full"
                        />
                    </UFormField>

                    <div class="xl:grid grid-cols-2 gap-4 space-y-4 xl:space-y-0">
                        <UFormField
                            label="Vorname"
                            required
                            class="w-full"
                        >
                            <UInput v-model="form.firstName"/>
                        </UFormField>
                        <UFormField
                            label="Nachname"
                            required
                            class="w-full"
                        >
                            <UInput v-model="form.lastName"/>
                        </UFormField>
                    </div>

                    <UFormField
                        label="Firma"
                    >
                        <UInput
                            v-model="form.company"
                            class="w-full"
                        />
                    </UFormField>

                    <UFormField
                        label="Zus. Info"
                    >
                        <UInput
                            v-model="form.extraInfo"
                            class="w-full"
                        />
                    </UFormField>

                    <div class="xl:grid grid-cols-2 gap-4 space-y-4 xl:space-y-0">
                        <UFormField
                            label="Straße"
                            required
                        >
                            <UInput
                                v-model="form.street"
                                class="w-full"
                            />
                        </UFormField>
                        <UFormField
                            label="Hausnummer"
                            required
                        >
                            <UInput
                                v-model="form.streetNo"
                                class="w-full"
                            />
                        </UFormField>
                    </div>

                    <div class="xl:grid grid-cols-2 gap-4 space-y-4 xl:space-y-0">
                        <UFormField
                            label="PLZ"
                            required
                        >
                            <UInput
                                v-model="form.zip"
                                class="w-full"
                            />
                        </UFormField>
                        <UFormField
                            label="Ort"
                            required
                        >
                            <UInput
                                v-model="form.city"
                                class="w-full"
                            />
                        </UFormField>
                    </div>

                    <UFormField
                        label="USt-ID"
                    >
                        <UInput
                            v-model="form.vatId"
                            class="w-full"
                        />
                    </UFormField>

                    <UFormField
                        label="Land"
                        required
                    >
                        <UInput
                            v-model="form.country"
                            class="w-full"
                        />
                    </UFormField>

                    <UFormField
                        label="Telefon"
                    >
                        <UInput
                            v-model="form.phone"
                            type="tel"
                            class="w-full"
                        />
                    </UFormField>

                    <UFormField
                        label="Telefax"
                    >
                        <UInput
                            v-model="form.fax"
                            type="tel"
                            class="w-full"
                        />
                    </UFormField>

                    <UFormField
                        label="Mobiltelefon"
                    >
                        <UInput
                            v-model="form.mobile"
                            type="tel"
                            class="w-full"
                        />
                    </UFormField>
                </div>
            </div>

            <div class="col-span-2 text-center">
                <UButton
                    label="Konto jetzt anlegen"
                    @click="register"
                />
            </div>
        </form>
    </main>
</template>

<script setup lang="ts">
import {z} from 'zod';
import {useForm} from 'vee-validate';
import {toTypedSchema} from '@vee-validate/zod';
import {until} from '@vueuse/core';
import PopupModal from '~/components/PopupModal.vue';

definePageMeta({
    path: '/konto-eroeffnen',
});

const router = useRouter();
const toast = useToast();

const salutationItems = [
    {
        label: 'Herr',
        value: 'mr',
    },
    {
        label: 'Frau',
        value: 'mrs',
    },
    {
        label: 'Divers',
        value: 'mx',
    },
];

const showModal = ref(false);
const submitting = ref(false);
const privacy = ref(false);

const form = reactive({
    email: '',
    plainPassword: '',
    passwordRepeat: '',
    address: 'mr',
    firstName: '',
    lastName: '',
    company: '',
    extraInfo: '',
    street: '',
    streetNo: '',
    zip: '',
    city: '',
    country: '',
    phone: '',
    fax: '',
    mobile: '',
    vatId: '',
});

const schema = toTypedSchema(
    z.object({
        email: z.string()
            .min(4)
            .max(255)
            .email('Bitte gültige E-Mail-Adresse eingeben.'),
        plainPassword: z.string()
            .min(5)
            .max(255),
        passwordRepeat: z.string()
            .min(5)
            .max(255),
        address: z.enum(['mr', 'mrs', 'mx']),
        firstName: z.string()
            .min(1)
            .max(255),
        lastName: z.string()
            .min(1)
            .max(255),
        company: z.string()
            .optional()
            .or(z.literal('')),
        extraInfo: z.string()
            .optional()
            .or(z.literal('')),
        street: z.string()
            .min(1)
            .max(255),
        streetNo: z.string()
            .min(1)
            .max(5),
        zip: z.string()
            .min(4)
            .max(5),
        city: z.string()
            .min(3)
            .max(255),
        country: z.string()
            .min(2)
            .max(255),
        phone: z.string()
            .optional()
            .or(z.literal('')),
        fax: z.string()
            .optional()
            .or(z.literal('')),
        mobile: z.string()
            .optional()
            .or(z.literal('')),
        vatId: z.string()
            .optional()
            .or(z.literal('')),
        privacy: z.literal(true, {errorMap: () => ({message: 'Bitte Datenschutzhinweise bestätigen.'})}),
    })
        .superRefine((val, ctx) => {
            if (val.plainPassword !== val.passwordRepeat) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ['passwordRepeat'],
                    message: 'Passwörter stimmen nicht überein.',
                });
            }
        }),
);

const {
    validate,
    errors,
} = useForm({
    validationSchema: schema,
    initialValues: {
        email: '',
        plainPassword: '',
        passwordRepeat: '',
        address: 'mr',
        firstName: '',
        lastName: '',
        company: '',
        extraInfo: '',
        street: '',
        streetNo: '',
        zip: '',
        city: '',
        country: '',
        phone: '',
        fax: '',
        mobile: '',
        vatId: '',
    },
});

async function register() {
    // const result = await validate(form as any);
    // if (!result.valid) return;

    submitting.value = true;
    try {
        // IMPORTANT: privacy nicht an API schicken, falls Backend es nicht kennt
        const {
            ...input
        } = form;

        const res = useAsyncGql({
            operation: 'createUser',
            variables: {input},
        });
        await until(res.pending)
            .toBe(false);

        if (res.error.value) {
            // GraphQL Fehlertext sauber rausziehen
            const msg = (res.error.value as any)?.data?.errors?.[0]?.message
                ?? (res.error.value as any)?.message
                ?? 'Unbekannter Fehler';
            throw new Error(msg);
        }

        toast.add({
            title: 'Vielen Dank für Ihre Registrierung!',
            description: 'Sie erhalten demnächst eine E-Mail von uns, mit der Sie Ihre Registrierung abschließend noch bestätigen müssen!',
            color: 'success',
        });
        await router.push('/anmelden');
    } catch (e: any) {
        toast.add({
            title: 'Fehler bei der Registrierung',
            description: e?.message ?? 'Unbekannter Fehler',
            color: 'error',
        });
    } finally {
        submitting.value = false;
    }
}
</script>
